import { useEffect, useState, useMemo } from 'react';
import api from '../utils/api';
import { Plus, Pencil, Trash2, CircleDot, Clock, Printer as PrinterIcon } from 'lucide-react';
import PageHeader from '../components/PageHeader';

const MATERIALS = ['경화강', '스테인레스', '황동', '텅스텐'];
const DIAMETERS = [0.4, 0.6];

function formatUsage(minutes) {
  if (!minutes) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
}

// 노즐 등록/수정 모달
function NozzleFormModal({ nozzle, onSave, onClose }) {
  const isEdit = !!nozzle?.id;
  const [type, setType] = useState(isEdit ? (nozzle.usageMinutes > 0 ? 'used' : 'new') : 'new');
  const [form, setForm] = useState({
    material: nozzle?.material || '경화강',
    diameter: nozzle?.diameter || 0.4,
    usageMinutes: nozzle?.usageMinutes || 0,
    notes: nozzle?.notes || '',
  });

  const handleSave = async () => {
    const data = { ...form };
    if (type === 'new' && !isEdit) data.usageMinutes = 0;
    if (isEdit) {
      await api.put(`/nozzles/${nozzle.id}`, data);
    } else {
      await api.post('/nozzles', data);
    }
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm">
        <h2 className="text-white font-bold mb-4">{isEdit ? '노즐 수정' : '노즐 등록'}</h2>
        <div className="space-y-3">
          {/* 새/사용 토글 (등록 시만) */}
          {!isEdit && (
            <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
              {[{ value: 'new', label: '새 노즐' }, { value: 'used', label: '사용한 노즐' }].map(t => (
                <button key={t.value} onClick={() => setType(t.value)}
                  className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    type === t.value ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'
                  }`}>{t.label}</button>
              ))}
            </div>
          )}
          {/* 재질 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">재질</label>
            <div className="flex gap-1.5">
              {MATERIALS.map(m => (
                <button key={m} onClick={() => setForm(f => ({ ...f, material: m }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                    form.material === m ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>{m}</button>
              ))}
            </div>
          </div>
          {/* 직경 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">직경</label>
            <div className="flex gap-2">
              {DIAMETERS.map(d => (
                <button key={d} onClick={() => setForm(f => ({ ...f, diameter: d }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.diameter === d ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>{d}mm</button>
              ))}
            </div>
          </div>
          {/* 누적 사용시간 — 사용한 노즐이거나 수정 시만 */}
          {(type === 'used' || isEdit) && (
            <div>
              <label className="text-gray-400 text-xs mb-1 block">누적 사용시간 (분)</label>
              <input type="number" value={form.usageMinutes}
                onChange={e => setForm(f => ({ ...f, usageMinutes: Math.max(0, +e.target.value) }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          )}
          {/* 메모 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">메모</label>
            <input value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="선택사항"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          <button onClick={handleSave} className="flex-1 bg-brand hover:bg-brand-600 text-white rounded-lg py-2 text-sm font-medium">저장</button>
        </div>
      </div>
    </div>
  );
}

// 노즐 카드
function NozzleCard({ nozzle, onEdit, onDelete }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <CircleDot size={16} className="text-brand" />
          <h3 className="text-white font-semibold text-sm">{nozzle.material} {nozzle.diameter}mm</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-1.5 text-gray-600 hover:text-white rounded-lg hover:bg-gray-800">
            <Pencil size={12} />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-600 hover:text-red-400 rounded-lg hover:bg-gray-800">
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* 사용시간 */}
      <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-3 py-2 mb-2">
        <Clock size={14} className="text-gray-500" />
        <span className="text-white text-sm font-medium">{formatUsage(nozzle.usageMinutes)}</span>
        <span className="text-gray-600 text-xs">사용</span>
      </div>

      {/* 장착 프린터 */}
      {nozzle.printer ? (
        <div className="flex items-center gap-2 bg-brand/10 rounded-lg px-3 py-2">
          <PrinterIcon size={14} className="text-brand" />
          <span className="text-brand text-xs font-medium">{nozzle.printer.name}</span>
          <span className="text-brand/60 text-xs">{nozzle.slotNo === 0 ? 'L' : 'R'}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-3 py-2">
          <PrinterIcon size={14} className="text-gray-600" />
          <span className="text-gray-500 text-xs">미장착</span>
        </div>
      )}

      {nozzle.notes && <p className="text-gray-500 text-xs mt-2">{nozzle.notes}</p>}
    </div>
  );
}

// 섹션 컴포넌트
function Section({ title, count, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-white font-semibold text-sm">{title}</h2>
        <span className="text-gray-500 text-xs">{count}개</span>
      </div>
      {children}
    </div>
  );
}

export default function NozzlePage() {
  const [nozzles, setNozzles] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.get('/nozzles').then(r => setNozzles(r.data));
  useEffect(() => { load(); }, []);

  const { mounted, used, fresh } = useMemo(() => {
    const m = [], u = [], f = [];
    for (const n of nozzles) {
      if (n.printerId) m.push(n);
      else if (n.usageMinutes > 0) u.push(n);
      else f.push(n);
    }
    return { mounted: m, used: u, fresh: f };
  }, [nozzles]);

  const del = async id => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/nozzles/${id}`);
    load();
  };

  const renderGrid = (list) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {list.map(n => (
        <NozzleCard key={n.id} nozzle={n}
          onEdit={() => setModal(n)}
          onDelete={() => del(n.id)} />
      ))}
    </div>
  );

  return (
    <div>
      <PageHeader icon={CircleDot} title="노즐 관리" count={nozzles.length}>
        <button onClick={() => setModal({})}
          className="flex items-center gap-2 bg-brand hover:bg-brand-600 text-white rounded-lg px-4 py-2 text-sm font-medium">
          <Plus size={16} /> 노즐 등록
        </button>
      </PageHeader>

      {/* 장착 중 */}
      {mounted.length > 0 && (
        <Section title="장착 중" count={mounted.length}>
          {renderGrid(mounted)}
        </Section>
      )}

      {/* 사용한 노즐 */}
      {used.length > 0 && (
        <Section title="사용한 노즐" count={used.length}>
          {renderGrid(used)}
        </Section>
      )}

      {/* 새 노즐 */}
      <Section title="새 노즐" count={fresh.length}>
        {fresh.length > 0 ? renderGrid(fresh) : (
          <div className="text-center py-10">
            <p className="text-gray-600 mb-2">새 노즐이 없습니다.</p>
            <button onClick={() => setModal({})} className="text-brand text-sm hover:underline">노즐 등록하기</button>
          </div>
        )}
      </Section>

      {modal !== null && (
        <NozzleFormModal
          nozzle={modal?.id ? modal : null}
          onSave={() => { load(); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
